/**
 * Phone number helpers. This is created because of ticket DIGIX-923.
 *
 * E.164 standard:
 *  - Minimum: a standard U.S. number (10 digits). Not really E.164 but requested in the ticket.
 *  - Maximum: 15 digits (E.164), excluding the leading "+".
 *  - A leading "+" denotes an explicit country code. Example: +54 Argentina
 *
 * Valid phone numbers would be: (123) 456-7654, +54 9 113 768 417 6
 */

export const E164_MAX_DIGITS = 15;
export const US_NSN_LENGTH = 10;

const COUNTRY_CODES: ReadonlySet<string> = new Set([
  '1',
  '7',
  '20',
  '27',
  '30',
  '31',
  '32',
  '33',
  '34',
  '36',
  '39',
  '40',
  '41',
  '43',
  '44',
  '45',
  '46',
  '47',
  '48',
  '49',
  '51',
  '52',
  '53',
  '54',
  '55',
  '56',
  '57',
  '58',
  '60',
  '61',
  '62',
  '63',
  '64',
  '65',
  '66',
  '81',
  '82',
  '84',
  '86',
  '90',
  '91',
  '92',
  '93',
  '94',
  '95',
  '98',
  '211',
  '212',
  '213',
  '216',
  '218',
  '220',
  '221',
  '222',
  '223',
  '224',
  '225',
  '226',
  '227',
  '228',
  '229',
  '230',
  '231',
  '232',
  '233',
  '234',
  '235',
  '236',
  '237',
  '238',
  '239',
  '240',
  '241',
  '242',
  '243',
  '244',
  '245',
  '246',
  '247',
  '248',
  '249',
  '250',
  '251',
  '252',
  '253',
  '254',
  '255',
  '256',
  '257',
  '258',
  '260',
  '261',
  '262',
  '263',
  '264',
  '265',
  '266',
  '267',
  '268',
  '269',
  '290',
  '291',
  '297',
  '298',
  '299',
  '350',
  '351',
  '352',
  '353',
  '354',
  '355',
  '356',
  '357',
  '358',
  '359',
  '370',
  '371',
  '372',
  '373',
  '374',
  '375',
  '376',
  '377',
  '378',
  '379',
  '380',
  '381',
  '382',
  '383',
  '385',
  '386',
  '387',
  '389',
  '420',
  '421',
  '423',
  '500',
  '501',
  '502',
  '503',
  '504',
  '505',
  '506',
  '507',
  '508',
  '509',
  '590',
  '591',
  '592',
  '593',
  '594',
  '595',
  '596',
  '597',
  '598',
  '599',
  '670',
  '672',
  '673',
  '674',
  '675',
  '676',
  '677',
  '678',
  '679',
  '680',
  '681',
  '682',
  '683',
  '685',
  '686',
  '687',
  '688',
  '689',
  '690',
  '691',
  '692',
  '850',
  '852',
  '853',
  '855',
  '856',
  '880',
  '886',
  '960',
  '961',
  '962',
  '963',
  '964',
  '965',
  '966',
  '967',
  '968',
  '970',
  '971',
  '972',
  '973',
  '974',
  '975',
  '976',
  '977',
  '992',
  '993',
  '994',
  '995',
  '996',
  '998',
  '800',
  '808',
  '870',
  '878',
  '881',
  '882',
  '883',
  '888',
  '979',
  '991'
]);

export function sanitizePhoneInput(raw: string): { hasPlus: boolean; digits: string } {
  if (!raw) return { hasPlus: false, digits: '' };

  const hasPlus = raw.trimStart().startsWith('+');
  const digits = raw.replace(/\D/g, '').slice(0, E164_MAX_DIGITS);

  return { hasPlus, digits };
}

export function splitCountryCode(digits: string): { countryCode: string; rest: string } {
  for (let len = 3; len >= 1; len--) {
    const candidate = digits.slice(0, len);
    if (candidate.length === len && COUNTRY_CODES.has(candidate)) {
      return { countryCode: candidate, rest: digits.slice(len) };
    }
  }
  return { countryCode: '', rest: digits };
}

export function formatPhoneForDisplay(raw: string): string {
  const { hasPlus, digits } = sanitizePhoneInput(raw);

  if (!digits) return hasPlus ? '+' : '';

  if (hasPlus) {
    const { countryCode, rest } = splitCountryCode(digits);

    if (countryCode === '1') {
      if (!rest) return '+1';
      if (rest.length <= 3) return `+1 (${rest}`;
      if (rest.length <= 6) return `+1 (${rest.slice(0, 3)}) ${rest.slice(3)}`;
      return `+1 (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6, 10)}`;
    }

    if (!countryCode) {
      const groups = digits.match(/.{1,3}/g) ?? [];
      return `+${groups.join(' ')}`;
    }

    if (!rest) return `+${countryCode}`;
    const groups = rest.match(/.{1,3}/g) ?? [];
    return `+${countryCode} ${groups.join(' ')}`;
  }

  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

export function normalizePhoneForSubmit(raw: string): string {
  const { hasPlus, digits } = sanitizePhoneInput(raw);
  if (!digits) return '';

  if (hasPlus) return `+${digits}`;

  return `+1${digits}`;
}

export function isValidPhone(raw: string): boolean {
  const { hasPlus, digits } = sanitizePhoneInput(raw);

  if (hasPlus) {
    return digits.length >= 8 && digits.length <= E164_MAX_DIGITS;
  }

  return digits.length === US_NSN_LENGTH;
}
